import { useMemo, useState } from "react";
import { Users, Ticket, UserCheck, PartyPopper, Baby, Sparkles } from "lucide-react";

const RANGES = [
  { key: "all", label: "All time", days: null },
  { key: "30", label: "30 days", days: 30 },
  { key: "90", label: "90 days", days: 90 },
  { key: "365", label: "1 year", days: 365 },
];

const pdate = (s) => {
  if (!s) return null;
  const m = String(s).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!m) return null;
  let y = +m[3];
  if (y < 100) y += 2000;
  return new Date(y, +m[1] - 1, +m[2]);
};
const tourDates = (t) => (Array.isArray(t.tour_date) ? t.tour_date : [t.tour_date]).map(pdate).filter(Boolean);

// Adult/child split for one tour: prefer explicit adults/children integers,
// else derive from the guest_list roster (a numeric age = child, blank age = adult).
const splitAdultsKids = (t) => {
  const a = Number(t.adults);
  const c = Number(t.children);
  if ((t.adults !== null && t.adults !== undefined && t.adults !== "" && !isNaN(a)) ||
      (t.children !== null && t.children !== undefined && t.children !== "" && !isNaN(c))) {
    return { adults: isNaN(a) ? 0 : a, kids: isNaN(c) ? 0 : c };
  }
  if (Array.isArray(t.guest_list) && t.guest_list.length) {
    let adults = 0, kids = 0;
    t.guest_list.forEach((g) => {
      const age = Number(g && g.age);
      if (g && g.age !== null && g.age !== undefined && String(g.age).trim() !== "" && !isNaN(age)) kids += 1;
      else adults += 1;
    });
    return { adults, kids };
  }
  return null;
};

const StatCard = ({ icon: Icon, tone, value, label, testid, className = "" }) => (
  <div className={`rounded-xl border border-zinc-800 bg-zinc-900/50 p-3.5 transition-transform hover:-translate-y-0.5 hover:border-[#9B2226]/50 ${className}`} data-testid={testid}>
    <div className={`mb-1.5 flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-800 ${tone}`}><Icon className="h-4 w-4" /></div>
    <p className="font-display text-2xl font-800 leading-none text-zinc-50">{value}</p>
    <p className="mt-1 text-xs text-zinc-400">{label}</p>
  </div>
);

export function GuestFacts({ tours = [], profiles = [] }) {
  const [range, setRange] = useState("all");

  const facts = useMemo(() => {
    const r = RANGES.find((x) => x.key === range);
    const now = new Date(); now.setHours(23, 59, 59, 999);
    const cutoff = r.days ? new Date(Date.now() - r.days * 86400000) : null;
    const nameToProfile = {};
    profiles.forEach((p) => { if (p.name) nameToProfile[p.name] = p; });

    const inRange = tours.filter((t) => {
      const name = (t.guest_name || "").trim();
      if (!name || name === "Unknown") return false;
      const ds = tourDates(t);
      if (!ds.length) return false;
      return ds.some((d) => d <= now && (!cutoff || d >= cutoff));
    });

    const guestSet = new Set(inRange.map((t) => t.guest_name.trim()));
    const people = inRange.reduce((sum, t) => sum + (Number(t.party_size) || 0), 0);
    const returning = [...guestSet].filter((n) => (nameToProfile[n]?.lifetime_tours || 0) > 1).length;

    let adults = 0, kids = 0;
    inRange.forEach((t) => {
      const s = splitAdultsKids(t);
      if (s) { adults += s.adults; kids += s.kids; }
    });

    const leadGuests = range === "all"
      ? profiles.filter((p) => p.name && p.name !== "Unknown").length
      : guestSet.size;

    return { leadGuests, tours: inRange.length, people, returning, adults, kids };
  }, [range, tours, profiles]);

  return (
    <div className="mb-5" data-testid="guest-facts">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-base font-800 tracking-tight text-zinc-100"><Sparkles className="h-4 w-4 text-[#E0575B]" /> Guest snapshot</h2>
        <div className="flex gap-1 rounded-lg border border-zinc-800 bg-zinc-900/60 p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`guest-facts-range-${r.key}`}
              className={`rounded-md px-2.5 py-1 text-xs font-600 transition-colors ${range === r.key ? "bg-[#9B2226] text-white" : "text-zinc-400 hover:text-zinc-200"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-6">
        <StatCard icon={Users} tone="text-[#E0575B]" value={facts.leadGuests} label="Lead Guests" testid="fact-guests" className="sm:col-span-2" />
        <StatCard icon={Ticket} tone="text-[#E0575B]" value={facts.tours} label={range === "all" ? "Tours hosted" : "Tours in range"} testid="fact-tours" className="sm:col-span-2" />
        <StatCard icon={PartyPopper} tone="text-[#E0575B]" value={facts.people.toLocaleString()} label="People hosted" testid="fact-people" className="sm:col-span-2" />
        <StatCard icon={UserCheck} tone="text-[#E0575B]" value={facts.returning} label="Returning Tours" testid="fact-returning" className="sm:col-span-3" />
        <StatCard icon={Baby} tone="text-[#E0575B]" value={`${facts.adults.toLocaleString()} / ${facts.kids.toLocaleString()}`} label="Adults / Kids" testid="fact-adults-kids" className="col-span-2 sm:col-span-3" />
      </div>
    </div>
  );
}
